/**
 * =============================================================================
 * NOTICE:
 * 
 * This module, along with its related sub-modules, is being developed and tested
 * in a central repository at https://github.com/palainteractive/web-geocomply-lib
 * 
 * IMPORTANT:
 * Any modifications directly to these source files will not be accepted unless 
 * they are submitted as a pull request, with adaquate documentation and associated 
 * tests, to the central repository:
 * 	https://github.com/palainteractive/web-geocomply-lib
 * 
 * By adhering to this process, we ensure that all changes are properly reviewed 
 * and integrated, maintaining the integrity and consistency of the codebase.
 * 
 * =============================================================================
 */

import { Injectable, OnDestroy } from "@angular/core";
import { LoadingController, Platform } from "@ionic/angular";
import { ReplaySubject, Observable, Subject, Subscription, asyncScheduler} from "rxjs";
import { Navigator } from "src/app/components/menu/navigator";
import { GeoComplyReasons, GeoComplyStatus, GeoResponse } from "src/app/interfaces/geo-interface";
import { CustomerModel } from "src/app/models/customer-model";
import { ConfigService } from "../config/config.service";
import { EventsService } from "../event/event.service";
import { UserSessionService } from "../user-session/user-session.service";
import { WebservicesService } from "../webservices/webservices.service";
import { GeoComplyService } from "./geo-comply.service";
import { GeoComplyOobeeService } from "./geo-comply-oobee.service";
import { filter, tap, switchMap } from "rxjs/operators";
//
import { scheduled, of, asapScheduler } from 'rxjs';
import { mergeAll } from 'rxjs/operators';
//
import { GeoComplyNDBSService } from "./geo-comply-ndbs.service";

@Injectable({
  providedIn: "root",
})
export class JurisdictionService implements OnDestroy {
  static readonly GEOCOMPLY_IP_CHECK_INTERVAL = 30 * 1000;

  private _jurisdictionSubject: Subject<GeoResponse>;
  private customer: CustomerModel;
  private customer$: ReplaySubject<CustomerModel> = new ReplaySubject(1);

  private _cached_geoResponse?: GeoResponse;
  private _cache_valid_until_ts: number;
  private tsLastResponse: number = 0;
  private has_been_verified: boolean = false;
  public _didUserAcceptReason: boolean = false; // user must accept reason for requesting geolocation permissions
  private initialized: boolean = false;

  private isAuthenticated: boolean = false;

  private reason: string;
  private isAllowed: boolean = false;
  private error_object: any;

  private FEATURE_GEOCOMPLY_USENODOWNLOAD: boolean = false;
  private FEATURE_GEOCOMPLY_OPTION_IPCHECK = true;
  private FEATURE_GEOCOMPLY_OPTION_RECHECK = true;
  private FEATURE_GEOCOMPLY_BUFFERTIME: number = 120;

  constructor(
    private config: ConfigService,
    private geoComply: GeoComplyService,
    private geoOobee: GeoComplyOobeeService,
    private geoNDBS: GeoComplyNDBSService,
    private events: EventsService,
    private platform: Platform,
    public userSession: UserSessionService,
    private readonly navigator: Navigator,
    private webservices: WebservicesService,
    private loadingCtrl:LoadingController
  ) {
    this._jurisdictionSubject = new Subject<GeoResponse>();
    
    this.config.isLoaded().then(() => {
      const configMap = this.config.getConfig();
      /** SET LOCAL FEATURE FLAGS: */
      this.FEATURE_GEOCOMPLY_USENODOWNLOAD = this.config.isFeatureON( configMap, "FEATURE_GEOCOMPLY_USENODOWNLOAD", undefined);
      this.FEATURE_GEOCOMPLY_OPTION_IPCHECK = this.config.isFeatureON( configMap, "FEATURE_GEOCOMPLY_OPTION_IPCHECK", true);
      this.FEATURE_GEOCOMPLY_OPTION_RECHECK = this.config.isFeatureON( configMap, "FEATURE_GEOCOMPLY_OPTION_RECHECK", true);
      this.FEATURE_GEOCOMPLY_BUFFERTIME = configMap['FEATURE_GEOCOMPLY_BUFFERTIME'] ? configMap['FEATURE_GEOCOMPLY_BUFFERTIME'] : 120;
      /** */

    });

    this.userSession.getCustomer().subscribe((customer: CustomerModel) => {

      if( undefined === customer)
        return;

      if( !customer || !customer.customerId) 
        return;
      if(
        (
          !this.customer
          || customer.customerId != this.customer.customerId
        )
      ) {

        this.customer = customer;
        this.reason = GeoComplyReasons.firstGeolocation;
        this.has_been_verified = false;
        this._cached_geoResponse = undefined;
        this._cache_valid_until_ts = 0;
        this.userSession.isAuthenticated().then((isAuthenticated) => {
          this.isAuthenticated = isAuthenticated;
        });
        this.customer$.next( customer);
      }
    });
    this.userSession.refreshCustomer();
  }

  ngOnDestroy(): void {
  }

  /**
   * Subscribes to jurisdiction geolocation updates.
   * Observers can listen for real-time updates regarding the user's geolocation status.
   *
   * @returns {Observable<GeoResponse>} An observable that emits geolocation responses when updated.
   */
  public listen(): Observable<GeoResponse> {
    return this._jurisdictionSubject;
  }

  /**
   * Pre-checks the user's permissions for geolocation access.
   * Determines whether the user has granted necessary permissions to proceed with geolocation verification.
   *
   * @returns {Promise<boolean>} A promise that resolves to `true` if permissions are granted, otherwise `false`.
   */

  public async permissionPrecheck(): Promise<boolean> {
    return await this.geoComply.permissionPrecheck();
  }

  private jurisdictionSwitch: Boolean = true; // Use this to switch geo on/off true means on false is off

/**
 * Verifies the user's jurisdiction for geolocation compliance.
 * This method ensures that the user's location adheres to regulatory requirements.
 * If verification is unsuccessful, an error is returned.
 *
 * @param retryFn {Function} A callback function invoked with the geolocation response to retry verification if necessary.
 * @returns {Promise<GeoResponse>} A promise that resolves with the geolocation response or rejects with an error.
 */
  public verifyJurisdiction(
    retryFn: (g: GeoResponse) => any
  ): Promise<GeoResponse> {

    if (!this.jurisdictionSwitch)
      return Promise.resolve({
        is_allowed: true,
        address: undefined,
      } as GeoResponse);

    return new Promise((resolve, reject) => {
      if (
        0 === parseInt(this.config.getConfig().FEATURE_GEOLOCATION_ENFORCED)
      ) {
        this.isJurisdictionVerificationInProgress = false 
        resolve({ is_allowed: true, address: undefined } as GeoResponse);
        return;
      }

      const runGeolocationCheck = () => {
        if (this.isAllowedJurisdiction()) {
          resolve(this._cached_geoResponse);
          this.isJurisdictionVerificationInProgress = false 
        } else {
          this.events.publish("event:geoComply:present");
          this.showGeoLoader();
          this._jurisdictionSubject.subscribe(
            (geoResponse: GeoResponse) => {
              this.events.publish("event:geoComply:dismiss");

              if( 614 == geoResponse.error?.error_code) {
                this._jurisdictionSubject.toPromise().then( resolve).catch( reject);
              }
              
              if (geoResponse.is_allowed) {
                this.has_been_verified = true;
                resolve(geoResponse);
              } else {
                reject(geoResponse);
              }
              this.isJurisdictionVerificationInProgress = false 
              this.dismissLoader()
            }
          );
          this.triggerGeoLocation(retryFn);
        }
      };

      if (this.shouldDisplayPermissionReason()) {
        this.displayPermissionReason().then((acceptsReason) => {
          this._didUserAcceptReason = acceptsReason;
          if (acceptsReason) {
            runGeolocationCheck();
          } else {  this.isJurisdictionVerificationInProgress = false }
        });
      } else {
        runGeolocationCheck();
      }
    });
  }


  /**
   * Debounces the jurisdiction verification process to avoid overlapping calls caused by calling code that does not
   * await either the success or Error result.
   * Ensures that only one jurisdiction verification process is active at any given time.
   * Note: a dummy GeoResponse with no reason is returned in the Promise when this condition is 
   * triggered.
   *
   * @param retryFn {Function} A callback function invoked with the geolocation response to retry verification if necessary.
   * @returns {Promise<GeoResponse>} A promise that resolves with the geolocation response or a default response if debounced.
   */
  protected isJurisdictionVerificationInProgress: boolean = false;
  public debounceVerifyJurisdiction(retryFn: (g: GeoResponse) => any): Promise<GeoResponse> {
      if (!this.isJurisdictionVerificationInProgress) {
          this.isJurisdictionVerificationInProgress = true;
          return this.verifyJurisdiction(retryFn)
      } else {
          return Promise.resolve({ is_allowed: false, address: '' });
      }
  }
  protected loadingDialog: HTMLIonLoadingElement | null = null;
  protected async showGeoLoader(): Promise<void> {
    try {
      const isGeoLoaderDisabled = this.config.getConfig().FEATURE_DISABLED_GEO_LOADING === '1';
      if (this.isJurisdictionVerificationInProgress && !isGeoLoaderDisabled) {
        this.loadingDialog = await this.loadingCtrl.create({
          message: 'Loading...',
          showBackdrop: true,
        });
        await this.loadingDialog.present();
      }
    } catch (error) {
      console.error('Error displaying geo loader:', error);
    }
  }
  protected async dismissLoader(): Promise<void> {
    try {
      if (this.loadingDialog) {
        await this.loadingDialog.dismiss();
        this.loadingDialog = null;
      }
    } catch (error) {
      console.error('Error dismissing geo loader:', error);
    }
  }

  public isAllowedJurisdiction(): boolean {
    return (
      this._cache_valid_until_ts &&
      this._cache_valid_until_ts > new Date().getTime() &&
      this._cached_geoResponse &&
      this._cached_geoResponse.is_allowed
    );
  }

  private funcToRetry: () => {} = null;
  protected async triggerGeoLocation(retryFn): Promise<void> {

    this.funcToRetry = retryFn ? retryFn : null;

    let numTries: number = 0;
    let mythis = this;
    const tryGeolocation = async () => {
      numTries++;

      if (mythis.geoComply.isConnected()) {
        mythis.tryGeocomply();
      } else {
        //console.log('geo waiting for connection.');
        if (numTries > 9) {
          const error = mythis.getGeoComplyError();
          this.emitGeoResponse({
            is_allowed: false,
            error: error,
          } as GeoResponse);
          return; // abort retries at this point
        }

        if (numTries % 10 == 0) {
          mythis.geoComply.reconnect();
        }

        setTimeout(() => {
          tryGeolocation();
        }, 750);
      }
    };

    try {
      await this.initialize();
    } catch( err) {
      this.initialized = false;
      return;
    }
    if( !this.initialized) {
      throw new Error( 'GEO: jurisdiction.service:: geoComply called while !initialized.   Check feature flag settings (FEATURE_GEOCOMPLY, etc.)');
    }

    tryGeolocation();
  }

  protected emitGeoResponse(geoResp: GeoResponse) {
    var was_allowed = this._cached_geoResponse && this._cached_geoResponse.is_allowed;
    this._cached_geoResponse = geoResp;
    let currTs = new Date().getTime();

    if (
      (!this.tsLastResponse) || (currTs - this.tsLastResponse > 4000) || (was_allowed && !geoResp.is_allowed)
    ) {
      // going from pass to fail
      this._jurisdictionSubject.next(geoResp);
    } else {
      console.warn('jurisdiction.ts: quashing: ', geoResp);
    }
    this.tsLastResponse = currTs;
  }

  public displayPermissionReason(): Promise<boolean> {
    return new Promise((resolve) => {
      const cssClass = "standard-popover";
      const popoverPromise = this.navigator.openPopover(
        "PreGeoInfoPopover",
        {},
        cssClass
      );

      popoverPromise.then((popover) => {
        if( popover) {
          popover!.onDidDismiss().then((data:any) => {
            console.log("Geolocation: Permission popover dismiss:", data);
            // this.setAcceptReason(!!data);
            resolve(data);
          });
        }
      });
    });
  }

  public shouldDisplayPermissionReason(): boolean {
    return (
      this.platform.is("cordova") &&
      this.platform.is("android") &&
      !this.didAcceptReason()
    );
    //return !this.didAcceptReason();
  }

  public setAcceptReason(didAccept) {
    window.localStorage.setItem(
      "geocomply_didAcceptPermissionReason",
      0 + didAccept
    );
  }

  public didAcceptReason() {
    var didAccept = window.localStorage.getItem(
      "geocomply_didAcceptPermissionReason"
    );
    return !!parseInt(didAccept);
  }

  public killConnection() {
    this.initialized = false;
    this.geoComply.killConnection();
  }

  public getFuncToRetry(): (g: GeoResponse) => {} {
    return this.funcToRetry;
  }

  private geoComplySubscription: Subscription;
  public async initialize(): Promise<void> {
    if (this.initialized) {
      // don't double bind if already initialized!
      return;
    }

    if (this.geoComplySubscription) {
      this.geoComplySubscription.unsubscribe();
    }

    await this.config.isLoaded();

    const clientapp = this.config.getClientApp();

    var observables: Array<Observable<GeoComplyStatus>> = 
    (!this.platform.is('cordova') && !this.platform.is('capacitor') && this.FEATURE_GEOCOMPLY_USENODOWNLOAD) ?
      [this.geoNDBS.getGeoStatuses()]
      :
      [ this.geoComply.getGeoComplyStatusObservable(),
        this.geoOobee.getGeoStatuses ? this.geoOobee.getGeoStatuses() : of(null)]
      ;

    const mergedObservable = scheduled( observables, asapScheduler).pipe(
      mergeAll(),
      filter((data) => data !== null)
    );

    this.geoComplySubscription = mergedObservable.subscribe(
      (geoComplyStatus: GeoComplyStatus) => {
        if (geoComplyStatus) {
          // null is when event trigger while user logged out.
          if (geoComplyStatus.error_code === 0) {
            console.log(
              "%cGeolocation: PLAYER IS ALLOWED.",
              "color: green; font-weight: bold"
            );
            this.isAllowed = true;
            this.error_object = undefined;
            //this.notificationApi.resume();              // resume SSE processing
          } else {
            console.log(
              "%cGeolocation: PLAYER IS DENIED.",
              "color: red; font-weight: bold"
            );
            this.isAllowed = false;
            this.error_object = geoComplyStatus;
          }

          if( geoComplyStatus.geolocate_in && geoComplyStatus.geolocate_in > 0) {
            var interval_s;

            interval_s =
              (geoComplyStatus.geolocate_in -
                this.getBufferTime(geoComplyStatus.geolocate_in));

            this._cache_valid_until_ts = new Date().getTime() + interval_s*1000;

            if( this.FEATURE_GEOCOMPLY_OPTION_RECHECK) {
              this.scheduleNextGeolocation( interval_s);
            }
          }
        } else {
          this.isAllowed = false;
        }

        //console.log('jurisdiction.ts: emitting GeoResponse: is_allowed=' + this.isAllowed + ' error_object: ' + JSON.stringify(this.error_object));
        this.emitGeoResponse({
          is_allowed: this.isAllowed,
          error: this.error_object,
        } as GeoResponse);
      }
    );

    try {
      await this.geoComply.initialize(clientapp);
      this.initialized = true;
    } catch( err) {
      this.initialized = false;
      throw new Error( 'GeoComply: '+err);
    }
  }

  protected getBufferTime(geolocate_in: number) {
    let bufferTimeForPopup =
      this.platform.is("mobileweb") && this.platform.is("android") ? 30 : 0;
    if (geolocate_in < this.FEATURE_GEOCOMPLY_BUFFERTIME) {
      return Math.min(geolocate_in * (30.0 / 100.0), bufferTimeForPopup + this.FEATURE_GEOCOMPLY_BUFFERTIME);
    } else {
      return Math.min(geolocate_in / 2, bufferTimeForPopup + this.FEATURE_GEOCOMPLY_BUFFERTIME);
    }
  }

  protected check(): Promise<boolean> {
    return new Promise(async (resolve) => {
      this.customer$.subscribe( customer => {
        if( !this.isAuthenticated)
          resolve(false);
        this.webservices.post("geoComply/check", {
          customer_id: customer.customerId,
          session_token: this.userSession.getSessionToken(),
        }).toPromise().then(() => {
          //console.log( 'check passed'); ONly show if Geo Check Fails
          resolve(true);
        })
        .catch((reason: any) => {
          console.log("Geolocation: Check failed!:", reason);
          resolve(false);
        });
      });
    });
  }

  public hasBeenVerifiedBefore(): boolean {
    return (
      !!this.config.getConfig().FEATURE_GEOLOCATION_ENFORCED ? this.has_been_verified : false
    );
  }

  public reconnect() {
    this.geoComply.reconnect();
  }

  public unsubscribe(): void {
    this.events.publish("event:geoComply:dismiss");
    //this.notificationApi.resume();
  }

  public resumeNotifications(): void {
    //this.notificationApi.resume();
  }

  public cancelGeolocation(): void {
  }

  private tryGeocomply(): void {
    this.customer$.subscribe( (customer) => {
      if (customer) {
        // guard against session timeout and checking after logout
        this.geoComply.handleGeolocationRequest(customer.customerId.toString(), this.reason);
      }
    });
  }

  private doGeoCheck(): void {
    this.reason = GeoComplyReasons.periodic;
    this.tryGeocomply();
  }

  private doCheck() {
    if( this.FEATURE_GEOCOMPLY_OPTION_IPCHECK
      && this.customer && this.isAuthenticated
    ) {
      this.check().then((geoIsOkay: boolean) => {
        if (geoIsOkay) {
          //console.log( 'IP is fine.'); Only show console message once IP change is detected.
        } else {
          console.log(
            "%cGeolocation: IP change detected, will perform geo check again",
            "color: red; font-weight: bold"
          );
          // will reschedule the next Scheduled Timeout for GeoCheck :
          this.scheduleNextGeolocation( 0);       // schedule it now
        }
      });
    }
  }

  private getGeoComplyError(): GeoComplyStatus {
    const status: GeoComplyStatus = {
      error_code: 612,
      error_message: undefined,
      error_details: undefined,
      geolocate_in: 0,
    };
    switch (true) {
      case this.geoComply.isMobileWeb:
        status.error_code = 103;
        break;
      case this.platform.is("cordova"):
      /** @todo need codes specific to Cordova installation */
      default:
    }
    return status;
  }

  public requestAndroidGeoPermissions() {
    if (this.shouldDisplayPermissionReason()) {
      this
        .displayPermissionReason()
        .then((acceptsReason: any) => {

          this._didUserAcceptReason = acceptsReason;
          //first dialog allow
          if (acceptsReason && typeof acceptsReason === 'object' && "data" in acceptsReason && acceptsReason.data) {

            this.requestNativePermissionsDialog(
              () => {
                /*all good, continue*/
                this.setAcceptReason(true);
              },
              (deny) => {
                console.log("Android geo ", deny);

                return this.navigator.navigateTo("AppRestrictedComponent", {
                  customModalClass: "app-restricted-screen",
                  animated: false,
                });

              }
            );

          } else {
            // first dialog cancel
            return this.navigator.navigateTo("AppRestrictedComponent", {
              customModalClass: "app-restricted-screen",
              animated: false,
            });
          }
        });
    }
  }
  
  requestNativePermissionsDialog(onSuccess, onError) {
    this.geoComply.requestRuntimePermissions(onSuccess, onError);
  }
  public getCachedGeoResponse() {
    return this._cached_geoResponse;
   }
   public emitGeoResponseFromCache() {
    if (this._cached_geoResponse) {
      this._jurisdictionSubject.next(this._cached_geoResponse);
    }
  }

  /**
   * Resets geolocation intervals and clears cached responses.
   * This method resets the cached geolocation response and schedules a new geolocation check,
   * the primary use of this method is in notification-api.service where IP-changed detection 
   * should trigger an immediate re-geolocation.
   */
  public resetGeolocationIntervals() {
    this._cache_valid_until_ts = 0;
    this._cached_geoResponse = undefined;
    this.scheduleNextGeolocation( 0);
  }

  private geoCheckTimeoutId: any;
  private scheduleNextGeolocation( geolocate_s: number) {
    if( this.geoCheckTimeoutId) {
      clearTimeout( this.geoCheckTimeoutId);
      this.geoCheckTimeoutId = 0;
    }
    this.geoCheckTimeoutId = setTimeout( this.doGeoCheck.bind( this), geolocate_s * 1000);
  }

  /** @deprecated IP change detection is implemented in notification-api.service */
  private ipCheckIntervalId: any;

  /** @deprecated IP change detection is implemented in notification-api.service */
  private cancelIpChecks() {
    throw new Error( 'DEPRECATED scheduleIpChecks -- IP change detection is handled in notification-api.service');
    if( this.ipCheckIntervalId) {
      clearTimeout( this.ipCheckIntervalId);
      this.ipCheckIntervalId = 0;
    }
  }

  /** @deprecated IP change detection is implemented in notification-api.service */
  private scheduleIpChecks() {
    throw new Error( 'DEPRECATED scheduleIpChecks -- IP change detection is handled in notification-api.service');
    this.cancelIpChecks();
    this.ipCheckIntervalId = setInterval( this.doCheck.bind( this), JurisdictionService.GEOCOMPLY_IP_CHECK_INTERVAL);
  }
}
